import { SHOTS, shotFallback, shotSrcSet, type ShotId } from '@/content/shots';

/**
 * 界面截图。优先给 AVIF，其次 WebP；两种都带 srcset，窄屏用不上大图。
 * width/height 写在 img 上，配合 `h-auto` 让浏览器提前占好位置，避免 CLS。
 *
 * 宽图在手机上按 34rem 固定宽度横向滚动：压到屏宽后界面文字会小到读不了，
 * 这和站内规格表的处理一致。
 */
export function Screenshot({
  id,
  sizes,
  caption,
  scrollOnSmall = false,
  className,
}: {
  id: ShotId;
  sizes: string;
  /** 覆盖 content 里的默认说明；传 null 表示不渲染。 */
  caption?: string | null;
  scrollOnSmall?: boolean;
  className?: string;
}) {
  const shot = SHOTS[id];
  const captionText = caption === undefined ? shot.caption : caption;

  const image = (
    <picture>
      <source type="image/avif" srcSet={shotSrcSet(id, 'avif')} sizes={sizes} />
      <source type="image/webp" srcSet={shotSrcSet(id, 'webp')} sizes={sizes} />
      <img
        src={shotFallback(id)}
        width={shot.width}
        height={shot.height}
        alt={shot.alt}
        loading="lazy"
        decoding="async"
        className="block h-auto w-full rounded-lg"
      />
    </picture>
  );

  return (
    <figure className={['flex flex-col', className].filter(Boolean).join(' ')}>
      <div className="rounded-xl border border-line bg-surface-1 p-1.5">
        {scrollOnSmall ? (
          <div className="overflow-x-auto">
            <div className="min-w-[34rem] md:min-w-0">{image}</div>
          </div>
        ) : (
          image
        )}
      </div>
      {captionText ? (
        <figcaption className="mt-3 text-xs leading-relaxed text-fg-muted">{captionText}</figcaption>
      ) : null}
    </figure>
  );
}
